import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { 
  FileText, CheckCircle, MessageSquare, Award, Clock,
  ExternalLink, Loader2, Video, File, ChevronDown, ChevronUp
} from 'lucide-react';
import ProfilePicture from '@/components/ProfilePicture';
import { AIHomeworkFeedbackHelper } from '@/components/AIHelpers';
import { SpeechToTextIcon } from '@/components/SpeechToText';
import TranslateButton from '@/components/TranslateButton';

const HomeworkReviewPanel = ({ user, onPointsEarned }) => {
  const [loading, setLoading] = useState(true);
  const [pendingHomework, setPendingHomework] = useState([]);
  const [reviewedHomework, setReviewedHomework] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedHomework, setSelectedHomework] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [activeView, setActiveView] = useState('pending'); // 'pending' or 'reviewed'
  const [viewedDocuments, setViewedDocuments] = useState(new Set()); // Track which documents have been opened
  
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  
  useEffect(() => {
    fetchData();
  }, []);
  
  // Mark a document as viewed when opened
  const markDocumentViewed = (homeworkId) => {
    setViewedDocuments(prev => new Set([...prev, homeworkId]));
  };
  
  const fetchData = async () => {
    try {
      setLoading(true);
      const [pendingRes, reviewedRes, statsRes] = await Promise.all([
        axios.get(`${API}/contributors/homework/pending`, { headers }),
        axios.get(`${API}/contributors/homework/reviewed?limit=20`, { headers }),
        axios.get(`${API}/contributors/homework/stats`, { headers })
      ]);
      setPendingHomework(pendingRes.data);
      setReviewedHomework(reviewedRes.data);
      setStats(statsRes.data);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Failed to fetch homework data:', error);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleReview = async (postId) => {
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/contributors/homework/${postId}/review`,
        {},
        { headers }
      );
      toast.success(`Homework reviewed! +${response.data.points_awarded} points`);
      fetchData();
      // Notify parent to refresh points
      if (onPointsEarned) onPointsEarned();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to review homework');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleSubmitFeedback = async () => {
    if (!selectedHomework || feedbackText.length < 10) {
      toast.error('Feedback must be at least 10 characters');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/contributors/homework/${selectedHomework.id}/feedback`,
        { feedback: feedbackText },
        { headers }
      );
      toast.success(`Feedback submitted! +${response.data.points_awarded} points`);
      setShowFeedbackModal(false);
      setFeedbackText('');
      setSelectedHomework(null);
      fetchData();
      // Notify parent to refresh points
      if (onPointsEarned) onPointsEarned();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };
  
  const openFeedbackModal = (homework) => {
    setSelectedHomework(homework);
    setFeedbackText('');
    setShowFeedbackModal(true);
  };
  
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };
  
  if (loading) {
    return (
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="py-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-cyan-500" />
          <p className="mt-2 text-gray-500">Loading homework...</p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4 pb-3 text-center">
              <Clock className="w-5 h-5 mx-auto mb-1 text-yellow-400" />
              <p className="text-2xl font-bold text-white">{stats.pending_homework}</p>
              <p className="text-xs text-gray-400">Pending Review</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4 pb-3 text-center">
              <CheckCircle className="w-5 h-5 mx-auto mb-1 text-green-400" />
              <p className="text-2xl font-bold text-white">{stats.total_reviewed}</p>
              <p className="text-xs text-gray-400">Total Reviewed</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4 pb-3 text-center">
              <MessageSquare className="w-5 h-5 mx-auto mb-1 text-blue-400" />
              <p className="text-2xl font-bold text-white">{stats.total_with_feedback}</p>
              <p className="text-xs text-gray-400">With Feedback</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4 pb-3 text-center">
              <Award className="w-5 h-5 mx-auto mb-1 text-cyan-400" />
              <p className="text-2xl font-bold text-cyan-400">
                {(stats.this_month?.points_from_reviews || 0) + (stats.this_month?.points_from_feedback || 0)}
              </p>
              <p className="text-xs text-gray-400">Points This Month</p>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* View Toggle */}
      <div className="flex gap-2">
        <Button
          variant={activeView === 'pending' ? 'default' : 'outline'}
          onClick={() => setActiveView('pending')}
          className={activeView === 'pending' ? 'bg-cyan-600' : ''}
        >
          <Clock className="w-4 h-4 mr-2" />
          Pending ({pendingHomework.length})
        </Button>
        <Button
          variant={activeView === 'reviewed' ? 'default' : 'outline'}
          onClick={() => setActiveView('reviewed')}
          className={activeView === 'reviewed' ? 'bg-cyan-600' : ''}
        >
          <CheckCircle className="w-4 h-4 mr-2" />
          Reviewed ({reviewedHomework.length})
        </Button>
      </div>
      
      {/* Homework List */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-white">
            {activeView === 'pending' ? 'Pending Homework Submissions' : 'Your Reviewed Homework'}
          </CardTitle>
          <CardDescription className="text-gray-400">
            {activeView === 'pending' 
              ? 'Review submissions to earn 2 points each. Add feedback for 2 additional points.'
              : 'Homework you have already reviewed'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {activeView === 'pending' ? (
            pendingHomework.length === 0 ? (
              <p className="text-center text-gray-500 py-8">
                🎉 All caught up! No pending homework to review.
              </p>
            ) : (
              <div className="space-y-3">
                {pendingHomework.map((hw) => (
                  <div 
                    key={hw.id}
                    className="bg-slate-700/50 rounded-lg p-4 border border-slate-600"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="secondary" className="text-xs">
                            {hw.category_name}
                          </Badge>
                          {hw.homework_is_video && (
                            <Badge className="bg-purple-600 text-xs">
                              <Video className="w-3 h-3 mr-1" />
                              Video
                            </Badge>
                          )}
                        </div>
                        <h4 className="font-medium text-white">{hw.title}</h4>
                        <p className="text-sm text-gray-400">
                          by {hw.username} • {formatDate(hw.created_at)}
                        </p>
                        
                        {/* Homework File Link - Always Visible */}
                        {hw.homework_file && (
                          <div className="mt-3 p-3 bg-cyan-900/30 border border-cyan-700/50 rounded-lg">
                            <p className="text-xs text-cyan-400 mb-2 font-semibold">📎 Homework Attachment (must open before reviewing):</p>
                            <div className="flex flex-wrap gap-3">
                              <a
                                href={`${API.replace('/api', '')}${hw.homework_file}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => markDocumentViewed(hw.id)}
                                className={`inline-flex items-center px-3 py-2 text-white text-sm rounded-lg transition-colors ${
                                  viewedDocuments.has(hw.id) 
                                    ? 'bg-green-600 hover:bg-green-700' 
                                    : 'bg-cyan-600 hover:bg-cyan-700'
                                }`}
                              >
                                {hw.homework_is_video ? <Video className="w-4 h-4 mr-2" /> : <File className="w-4 h-4 mr-2" />}
                                {viewedDocuments.has(hw.id) ? '✓ Opened: ' : 'Open '}{hw.homework_filename || 'Homework File'}
                                <ExternalLink className="w-3 h-3 ml-2" />
                              </a>
                              {hw.homework_pdf && (
                                <a
                                  href={`${API.replace('/api', '')}${hw.homework_pdf}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() => markDocumentViewed(hw.id)}
                                  className={`inline-flex items-center px-3 py-2 text-white text-sm rounded-lg transition-colors ${
                                    viewedDocuments.has(hw.id) 
                                      ? 'bg-green-600 hover:bg-green-700' 
                                      : 'bg-purple-600 hover:bg-purple-700'
                                  }`}
                                >
                                  <FileText className="w-4 h-4 mr-2" />
                                  {viewedDocuments.has(hw.id) ? '✓ ' : ''}View PDF Version
                                  <ExternalLink className="w-3 h-3 ml-2" />
                                </a>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Expandable Post Content */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setExpandedItem(expandedItem === hw.id ? null : hw.id)}
                          className="mt-2 text-gray-400 hover:text-white p-0 h-auto"
                        >
                          {expandedItem === hw.id ? (
                            <><ChevronUp className="w-4 h-4 mr-1" /> Hide Post Content</>
                          ) : (
                            <><ChevronDown className="w-4 h-4 mr-1" /> Show Post Content</>
                          )}
                        </Button>
                        
                        {expandedItem === hw.id && (
                          <div className="mt-3 p-3 bg-slate-800/50 rounded-lg">
                            <p className="text-sm text-gray-300 whitespace-pre-wrap">{hw.content}</p>
                            {hw.content && (
                              <TranslateButton text={hw.content} className="mt-2" />
                            )}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex flex-col items-end gap-2 ml-4">
                        <Button
                          onClick={() => handleReview(hw.id)}
                          disabled={submitting || !viewedDocuments.has(hw.id)}
                          className={`${
                            viewedDocuments.has(hw.id) 
                              ? 'bg-green-600 hover:bg-green-700' 
                              : 'bg-gray-500 cursor-not-allowed'
                          }`}
                          title={!viewedDocuments.has(hw.id) ? 'Please open the homework file first' : ''}
                        >
                          {submitting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <><CheckCircle className="w-4 h-4 mr-1" /> Review (+2 pts)</>
                          )}
                        </Button>
                        {!viewedDocuments.has(hw.id) && (
                          <p className="text-xs text-yellow-400">Open file first</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            reviewedHomework.length === 0 ? (
              <p className="text-center text-gray-500 py-8">
                You have not reviewed any homework yet.
              </p>
            ) : (
              <div className="space-y-3">
                {reviewedHomework.map((hw) => (
                  <div 
                    key={hw.id}
                    className="bg-slate-700/50 rounded-lg p-4 border border-slate-600"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-white">{hw.title}</h4>
                        <p className="text-sm text-gray-400">
                          by {hw.username} • Reviewed: {formatDate(hw.homework_reviewed_at)}
                        </p>
                        
                        {hw.homework_feedback ? (
                          <div className="mt-2 p-2 bg-green-900/30 rounded border border-green-700/50">
                            <p className="text-xs text-green-400 mb-1">Your Feedback:</p>
                            <p className="text-sm text-gray-300">{hw.homework_feedback}</p>
                            <TranslateButton text={hw.homework_feedback} className="mt-1" />
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openFeedbackModal(hw)}
                            className="mt-2"
                          >
                            <MessageSquare className="w-4 h-4 mr-1" />
                            Add Feedback (+2 pts)
                          </Button>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Badge className="bg-green-600">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Reviewed
                        </Badge>
                        {hw.homework_feedback && (
                          <Badge className="bg-blue-600">
                            <MessageSquare className="w-3 h-3 mr-1" />
                            Feedback
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </CardContent>
      </Card>
      
      {/* Feedback Modal */}
      <Dialog open={showFeedbackModal} onOpenChange={setShowFeedbackModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Feedback</DialogTitle>
            <DialogDescription>
              Provide constructive feedback for &ldquo;{selectedHomework?.title}&rdquo;. You will earn 2 points for adding feedback.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex justify-end gap-2">
              <AIHomeworkFeedbackHelper
                homeworkTitle={selectedHomework?.title || 'Homework'}
                homeworkDescription={selectedHomework?.content}
                studentName={selectedHomework?.author || 'Student'}
                onInsert={(text) => setFeedbackText(text)}
              />
            </div>
            <div className="relative">
              <Textarea
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder="Write your feedback here... (minimum 10 characters)"
                rows={5}
                className="resize-none pr-12"
              />
              <div className="absolute bottom-2 right-2">
                <SpeechToTextIcon 
                  onTranscript={(text) => setFeedbackText(feedbackText + ' ' + text)}
                />
              </div>
            </div>
            <p className="text-xs text-gray-500">
              {feedbackText.length} characters {feedbackText.length < 10 && `(${10 - feedbackText.length} more needed)`}
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowFeedbackModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmitFeedback}
                disabled={submitting || feedbackText.length < 10}
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                ) : (
                  <><MessageSquare className="w-4 h-4 mr-2" /> Submit Feedback</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomeworkReviewPanel;
